import {
  convertFromHTML,
  convertFromRaw,
  convertToRaw,
  ContentState,
  EditorState,
  SelectionState,
  Modifier,
  ContentBlock,
  genKey,
} from 'draft-js';
import get from 'lodash/get';

import { isJsonString } from 'Shared/helpers';

export function createEmptyEditorState() {
  return EditorState.createEmpty();
}

export function jsonEmptyContentState() {
  const contentStateEmpty = EditorState.createEmpty().getCurrentContent();
  return JSON.stringify(convertToRaw(contentStateEmpty));
}

export function jsonContentStateFromString(string) {
  return JSON.stringify(convertToRaw(ContentState.createFromText(string)));
}

export function createEmptySelectionState(key, anchorOffset, focusOffset) {
  return SelectionState.createEmpty(key).merge({ anchorOffset, focusOffset });
}

export function contentStateWithReplacedText(contentState, selectionState, replaceWithString) {
  return Modifier.replaceText(contentState, selectionState, replaceWithString);
}

export function contentStateWithInsertedText(contentState, selectionState, insertedText) {
  return Modifier.insertText(contentState, selectionState, insertedText);
}

export function contentStateWithAppliedEntity(contentState, selectionState, entityKey) {
  return Modifier.applyEntity(contentState, selectionState, entityKey);
}

export function editorStateFromJson(jsonString) {
  const rowState = JSON.parse(jsonString);
  const editorContent = convertFromRaw(rowState);
  return EditorState.createWithContent(editorContent);
}

export function editorStateFromJsonWithDecorator(jsonString, decorator) {
  const rowState = JSON.parse(jsonString);
  const editorContent = convertFromRaw(rowState);
  return EditorState.createWithContent(editorContent, decorator);
}

export function editorStateFromHtml(htmlString) {
  if (!htmlString) return createEmptyEditorState();
  const blocksFromHTML = convertFromHTML(htmlString);
  const stateToHTML = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
  return EditorState.createWithContent(stateToHTML);
}

export function editorStateToJsonString(editorState) {
  const contentState = editorState.getCurrentContent();
  return JSON.stringify(convertToRaw(contentState));
}

export function editorStateToContentBlocks(editorState) {
  const contentState = editorState.getCurrentContent();
  return get(convertToRaw(contentState), 'blocks', []);
}

export function moveSelectionToEnd(editorState) {
  const content = editorState.getCurrentContent();
  const blockMap = content.getBlockMap();
  const key = blockMap.last().getKey();
  const length = blockMap.last().getLength();
  const selection = new SelectionState({
    anchorKey: key,
    anchorOffset: length,
    focusKey: key,
    focusOffset: length,
  });
  return EditorState.forceSelection(editorState, selection);
}

export function editorStateForEditorComponent(body) {
  if (!body || !body.length) return EditorState.createEmpty();
  if (isJsonString(body)) return editorStateFromJson(body);
  return editorStateFromHtml(body);
}

export function textOfEditorStateFromJson(jsonString) {
  return editorStateForEditorComponent(jsonString)?.getCurrentContent()?.getPlainText();
}

export function isEmptyEditor(editorState) {
  const hasText = editorState.getCurrentContent().getPlainText().replace(/\s/g, '');
  const hasEntity = Object.keys(convertToRaw(editorState.getCurrentContent()).entityMap).length;
  return !hasText && !hasEntity;
}

export function getContentStateWithAppliedEntity(contentState, selection, entityType, data, mutability = 'IMMUTABLE') {
  const contentStateWithEntity = contentState.createEntity(entityType, mutability, data);
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  return contentStateWithAppliedEntity(contentState, selection, entityKey);
}

export function inserNewBlockToContentState(contentState, blockType = 'unstyled') {
  const blockMap = contentState.getBlockMap();
  const newBlock = new ContentBlock({
    key: genKey(),
    text: '',
    type: blockType,
  });
  const newBlockMap = blockMap
    .toSeq()
    .concat([[newBlock.getKey(), newBlock]])
    .toOrderedMap();
  return contentState.merge({ blockMap: newBlockMap });
}
